/**
 * vibrantPreset.ts
 *
 * Parses stored Vibrant preset / prior-order content into the shared
 * ParsedLabPreset shape the order modal consumes. Unlike Quest (which stores a
 * raw HL7 string), Vibrant presets store a small JSON blob
 *   { routingId, tests: [{ testCode, description, obrNotes, routingId? }] }
 * and prior orders are normalized to the same shape server-side. The top-level
 * routingId is the location the preset / order is for ('' = all locations);
 * a test's routingId is the location its code belongs to ('' = everywhere,
 * absent = unknown). Vibrant has no facility / insurance / diagnoses, so only
 * the location and tests are hydrated.
 */
import type { ParsedLabPreset } from './LabOrderModalCore';
import type { QuestOrderNote } from '@/services/labs.service';

interface RawVibrantTest {
  testCode?: string;
  description?: string;
  obrNotes?: QuestOrderNote[];
  routingId?: string;
}

export function parseVibrantPreset(content: string, _kind: 'order' | 'preset', defaultDate: string): ParsedLabPreset {
  let tests: RawVibrantTest[] = [];
  let routingId = '';
  try {
    const parsed = JSON.parse(content || '{}');
    if (Array.isArray(parsed?.tests)) {
      tests = parsed.tests as RawVibrantTest[];
    }
    if (typeof parsed?.routingId === 'string') {
      routingId = parsed.routingId;
    }
  } catch {
    tests = [];
  }

  return {
    routingId,
    pidNotes: [],
    insurance: [],
    diagnoses: [],
    tests: tests
      .filter((t) => (t.testCode ?? '').trim() !== '')
      .map((t) => ({
        testCode: String(t.testCode),
        description: String(t.description ?? ''),
        collectionDate: defaultDate,
        obrNotes: Array.isArray(t.obrNotes) ? t.obrNotes : [],
        ...(typeof t.routingId === 'string' ? { routingId: t.routingId } : {}),
      })),
  };
}
